<template>
	<section v-if="visibleForm && maxCount" v-editable="blok" :class="{'container': container}">
		<div class="text-center max-w-screen-md mb-11 mx-auto">
			<h2 class="typo-h1">
				{{ blok.formName }}
			</h2>
			<p v-if="blok.formPerex" class="mt-4 md:mt-6">
				{{ blok.formPerex }}
			</p>
		</div>

		<FormKit
			id="pageForm"
			#default="{state: {valid}}"
			v-model="model"
			type="form"
			form-class="mt-6 [&_ul]:!mb-0 [&_ul]:!list-none [&_ul]:!pl-0"
			:actions="false"
			:incomplete-message="false"
			@submit="submit"
		>
			<div
				class="max-w-[42.625rem] mx-auto bg-background-secondary p-6 rounded-lg grid grid-cols-1 md:grid-cols-[15.125rem_1fr] md:gap-10 gap-8"
			>
				<div class="text-base">
					<h3 class="font-semibold text-heading-secondary mb-2">
						{{ blok.groupContactInfoName }}
					</h3>
					<p class="text-body-primary typo-body-s">
						{{ blok.groupContactInfoPerex }}
					</p>
				</div>

				<div class="space-y-4">
					<FormKit
						type="text"
						name="name"
						validation="required|alpha_spaces"
						:validation-messages="{
							required: 'Vyplňte jméno',
							alpha_spaces: 'Jméno nesmí obsahovat speciální symboly nebo čísla'
						}"
						placeholder="Zadejte jméno"
						:classes="{input: 'max-w-none !py-2.5'}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Jméno <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>

					<FormKit
						type="text"
						name="surname"
						validation="required|alpha_spaces"
						:validation-messages="{
							required: 'Vyplňte příjmení',
							alpha_spaces: 'Příjmení nesmí obsahovat speciální symboly nebo čísla'
						}"
						placeholder="Zadejte příjmení"
						:classes="{input: 'max-w-none !py-2.5'}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Příjmení <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldPhone"
						type="tel"
						label="Telefonní číslo"
						name="phone"
						validation="required|phoneCustomRule"
						:validation-messages="{
							required: 'Vyplňte telefonní číslo',
						}"
						:classes="{input: 'max-w-none !py-2.5'}"
						autocomplete="tel"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Telefonní číslo <span class="text-body-important">*</span>
							</label>
						</template>
						<template #prefix>
							<span class="pl-3 pr-2 typo-body-s text-body-primary">+420</span>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldEmail"
						type="email"
						label="E-mail"
						name="email"
						placeholder="Váš email"
						validation="required|emailCustomRule"
						:validation-messages="{
							required: 'Vyplňte prosím email.',
						}"
						prefix-icon="email"
						:classes="{input: 'max-w-none !py-2.5'}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								E-mail <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldEan"
						type="number"
						label="Číslo vaší karty (EAN)"
						name="ean"
						validation="required|length:13,13"
						:validation-messages="{
							length: 'EAN karta musí mít 13 číslic'
						}"
						placeholder="Naleznete na zadní straně karty"
						:classes="{input: 'max-w-none !py-2.5'}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Číslo vaší karty (EAN) <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldIco"
						type="number"
						label="IČO"
						name="ico"
						validation="required|length:8"
						:validation-messages="{
							length: 'IČO musí mít 8 číslic'
						}"
						placeholder="Zadejte 8 místné číslo"
						:classes="{input: 'max-w-none !py-2.5'}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								IČO <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldReceipt"
						type="number"
						label="Číslo účtenky"
						name="receipt"
						placeholder="Zadat číslo"
						:classes="{input: 'max-w-none !py-2.5', inner: `border border-border-secondary ${!receiptNumberIsValid ? 'ring-functional-error-primary!' : ''}`}"
						validation="required|length:4,36"
						:errors="[receiptNumberIsValid ? '' : 'Číslo účtenky není platné nebo již byla použita.']"
						:validation-messages="{
							length: 'Číslo účtenky musí mít mezi 4 a 36 znaky.'
						}"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Číslo účtenky
								<span class="text-body-important">*</span>
								<button class="rounded-full focus-outline" type="button" @click="toggleReceiptModal()">
									<NuxtIcon name="info" class="text-icon-primary" filled />
								</button>
							</label>
						</template>
						<template #suffixIcon>
							<NuxtIcon
								v-if="status === 'pending'"
								name="custom/loader"
								class="text-icon-primary p-2 animate-spin"
								filled
							/>
						</template>
					</FormKit>
					<FormKit
						v-if="blok.groupContactInfoFieldHypermarket"
						type="select"
						label="Hypermarket"
						name="hypermarket"
						validation="required"
						:placeholder="selectHypermarket"
						:classes="{input: 'max-w-none !py-2.5'}"
						:options="hypermarketsOption"
					>
						<template #label>
							<label class="form-label flex items-center gap-1.5">
								Hypermarket <span class="text-body-important">*</span>
							</label>
						</template>
					</FormKit>
				</div>

				<div class="text-base">
					<h3 class="font-semibold text-heading-secondary mb-2">
						{{ blok.groupContentName }}
					</h3>
					<p class="text-body-primary typo-body-s">
						{{ blok.groupContentPerex }}
					</p>
				</div>

				<div class="space-y-4">
					<FormKitSchema :schema="schemaModelContent" />

					<FormKit
						type="submit"
						:label="blok.submitFormLabel"
						:disabled="!valid || status === 'pending' || loading || !receiptNumberIsValid"
						:classes="{input: 'btn btn-primary btn-lg !py-3 px-16 w-full max-w-[25rem] justify-center'}"
					/>
				</div>
			</div>
		</FormKit>

		<ClientTeleport>
			<TransitionGroup name="fade">
				<ModalReceiptInfo
					v-if="visibleReceiptModal"
					key="receipt-modal"
					@update:is-open="(newValue: boolean) => visibleReceiptModal = newValue"
				/>
				<ModalFormInfo
					v-if="isSent"
					key="form-info-modal"
					@update:is-open="(newValue: boolean) => isSent = newValue"
				>
					<template #heading>
						Formulář byl úspěšně odeslán
					</template>
				</ModalFormInfo>
			</TransitionGroup>
		</ClientTeleport>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {isWithinInterval, isAfter, isBefore} from 'date-fns';
	import {watchDebounced} from '@vueuse/core';
	import {reset} from '@formkit/core';
	import {useStoryblokHypermarketsStore} from '@/stores/storyblokHypermarkets';
	import {CmsService, type CmsFormData} from '@/openapi';
	import type {PageTypeFormStoryblok} from '~/storyblok/types';
	import {useCustomerProfileStore} from '@/stores/customerProfile';
	import {isStoryblokStory} from '~/utils/stories';

	type ContentField = {
		fieldUid: string;
		fieldType: string;
		fieldValue: string | string[];
	};
	type FormData = Record<string, any>;

	interface Props {
		blok: PageTypeFormStoryblok;
		formId: string;
		container?: boolean;
	}

	const props = defineProps<Props>();
	const hypermarketsStore = useStoryblokHypermarketsStore();

	const selectHypermarket = ref('Vyberte hypermarket');
	const now = new Date();
	const {executeRecaptcha} = useGoogleRecaptcha();
	const [visibleReceiptModal, toggleReceiptModal] = useToggle();
	const customerProfileStore = useCustomerProfileStore();
	const {profile} = storeToRefs(customerProfileStore);
	const isSent = ref(false);
	const loading = ref(false);
	const {selectedHypermarketInstance, hypermarkets} = storeToRefs(hypermarketsStore);
	const formHypermarket = computed(() => (
		isStoryblokStory(props.blok.formForHypermarket) ? props.blok.formForHypermarket : undefined
	));
	const visibleForm = computed(() => {
		const hypermarket = selectedHypermarketInstance.value?.content.gsoaId === formHypermarket.value?.content.gsoaId || formHypermarket.value?.content.gsoaId === '4000';
		if (!hypermarket) {
			return false;
		}

		const durationStart = props.blok.durationStart ? new Date(props.blok.durationStart) : undefined;
		const durationEnd = props.blok.durationEnd ? new Date(props.blok.durationEnd) : undefined;

		if (durationStart && durationEnd) {
			return isWithinInterval(now, {start: durationStart, end: durationEnd});
		}
		if (durationStart) {
			return isAfter(now, durationStart);
		}
		if (durationEnd) {
			return isBefore(now, durationEnd);
		}

		return true;
	});

	const model = ref({
		phone: '',
		email: '',
		name: '',
		surname: '',
		ean: undefined,
		ico: undefined,
		receipt: undefined,
		hypermarket: undefined,
	});

	const schemaModelContent = computed(() => getFormSchema(props.blok.groupContentFields) as import('@formkit/core').FormKitSchemaDefinition);
	const hypermarketsOption = computed(() => hypermarkets.value?.map((hm) => ({
		label: hm.content.name,
		value: hm.slug,
	})) ?? []);
	const {data: validation, status, refresh} = await useLazyAsyncData(
		'form-validate',
		async () => (await CmsService.postApiVCmsFormValidate({
			requestBody: {
				formId: props.formId,
				receiptNumber: model.value.receipt ? String(model.value.receipt) : undefined,
			},
		})) ?? null,
	);

	const formForHypermarketSlug = computed(() => formHypermarket.value?.slug);
	const maxCount = computed(() => validation.value?.maxCount === 'valid' || validation.value?.maxCount === 'not_checked');
	const receiptNumber = computed(() => model.value.receipt);
	const receiptNumberIsValid = computed(() => validation.value?.receiptNumber !== 'invalid');
	const formDataForSend = computed(() => transformContentFields(model.value));

	onMounted(() => {
		const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

		if (navigation?.type === 'back_forward') {
			reset('pageForm');
		}
	});

	watchDebounced(
		receiptNumber,
		() => refresh(),
		{debounce: 1000, maxWait: 3000},
	);

	watch(profile, () => {
		if (profile.value) {
			model.value.name = profile.value.personalInfo?.name ?? '';
			model.value.surname = profile.value.personalInfo?.surname ?? '';
			model.value.email = profile.value.contacts?.email ?? '';
			model.value.phone = profile.value.contacts?.phone ?? '';
			model.value.ean = profile.value.account?.card?.eanGlobusCard ?? '';
		}
	}, {immediate: true});

	async function submit() {
		loading.value = true;
		const {token} = await executeRecaptcha('postApiVCmsFormData');

		const formData = {
			formId: props.formId,
			formName: props.blok.formName,
			firstname: model.value.name ?? null,
			lastname: model.value.surname,
			phone: model.value.phone,
			email: model.value.email,
			receiptNumber: model.value.receipt,
			globusEanCard: model.value.ean,
			ico: model.value.ico,
			hypermarket: formForHypermarketSlug.value === 'globus' ? null : formForHypermarketSlug.value,
			...(model.value.hypermarket !== selectHypermarket.value && {formSelectedHypermarket: model.value.hypermarket}),
			gdpr: true,
			note: '',
			token,
			...formDataForSend.value,
		};

		try {
			await CmsService.postApiVCmsFormData({requestBody: formData as CmsFormData});
			isSent.value = true;
			reset('pageForm');
		} catch (e: any) {
			throw createError({
				message: 'Failed to send form',
				cause: e,
			});
		} finally {
			loading.value = false;
		}
	}

	function transformContentFields(data: FormData): {content: ContentField[]} {
		const content: ContentField[] = Object.entries(data)
			.filter(([key]) => key.startsWith('content.'))
			// .filter(([key, value]) => key.startsWith('content.') && value !== '' && value !== undefined && (!Array.isArray(value) || value.length > 0))
			.map(([key, value]) => {
				const [, fieldUid, fieldType] = key.split('.');

				return {
					fieldUid,
					fieldType,
					fieldValue: value ?? '',
				};
			});

		return {content};
	}
</script>
